// Copyright (c) Docugami, Inc. All rights reserved.

import { CommandBar, ICommandBarStyles, IContextualMenuItem, Icon, TooltipHost } from "@fluentui/react";
import { backgroundGray, themeBlue } from "components/shell/presentation/AppStyles";
import { DgIconButton } from "components/shell/utils/DgIconButton";
import { dgIconNames } from "hooks/dgIcons";
import { Ref, forwardRef } from "react";
import { ConnectDragSource } from "react-dnd";
import styled from "styled-components";
import { AutomationProps } from "utils/Automation";
import { CardContainer, GrabHandleContainer, ItemCardletStyles, getClassNames } from "./CardStyles";

const ContentRoot = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;
    padding-bottom: 4px;
    >:first-child {
        border-bottom: 1px solid ${backgroundGray};
    }
    >div {
        padding: 4px 7px 0px 7px;
        box-sizing: border-box;
    }
`

const IconButtonsContainer = styled.div`
    display: flex;
    gap: 8px;
`

const Header = styled.div`
    display: flex;
    flex-direction: row;
    width: 100%;
    gap: 5px;
`

const LabelContainer = styled.div`
    flex: 1;
    align-self: center;
    margin-left: 10px;
    padding-bottom: 5px;
`

const Body = styled.div`
    flex: 1;
    width: 100%;
    max-height: 100%;
    overflow: auto;
`

const Footer = styled.div`
    display: flex;
    width: 100%;
`

export interface ItemCardletProps extends AutomationProps {
    header?: JSX.Element;
    children?: JSX.Element | JSX.Element[];
    footer?: JSX.Element;
    collapsed?: boolean;
    dragHandleRef?: ConnectDragSource;
    onRemove?: () => void;
    canRemove?: boolean;
    styles?: ItemCardletStyles;
    onExamplesClick?: () => void;
    overflowCommands?: IContextualMenuItem[];
    headerCommands?: IContextualMenuItem[];
}

export const ItemCardlet = forwardRef((props: ItemCardletProps, ref?: Ref<HTMLDivElement>): JSX.Element => {
    if (props.collapsed) {
        return (
            <CardContainer
                ref={ref}
                data-automation-id={props.automationId}
                className={getClassNames(props.styles?.root)}>
                <ContentRoot>
                    <CardletHeader
                        commands={props.headerCommands}
                        onRemove={props.onRemove}
                        canRemove={props.canRemove}
                        label={props.header}
                        dragHandleRef={props.dragHandleRef} />
                </ContentRoot>
            </CardContainer>
        )
    }

    return (
        <CardContainer
            ref={ref}
            className={getClassNames(props.styles?.root)}
            data-automation-id={props.automationId}>
            <ContentRoot>
                <CardletHeader
                    onRemove={props.onRemove}
                    canRemove={props.canRemove}
                    label={props.header}
                    dragHandleRef={props.dragHandleRef}
                    onExamplesClick={props.onExamplesClick}
                    commands={props.headerCommands}
                    overflowCommands={props.overflowCommands} />
                <Body>
                    {props.children}
                </Body>
                {!!props.footer && <Footer>
                    {props.footer}
                </Footer>}
            </ContentRoot>
        </CardContainer>
    )
})

interface CardletHeaderProps {
    label?: JSX.Element,
    dragHandleRef?: ConnectDragSource,
    onRemove?: () => void,
    canRemove?: boolean,
    onExamplesClick?: () => void;
    commands?: IContextualMenuItem[]
    overflowCommands?: IContextualMenuItem[];
}

export const CardletHeader = (props: CardletHeaderProps): JSX.Element => {
    const commandBarStyles: Partial<ICommandBarStyles> = {
        root: {
            height: 'unset',
            padding: 0,
            margin: '2px',
            minWidth: !!props.commands?.length ? '50px' : 'unset',
            ".ms-CommandBarItem-link": {
                minWidth: '20px',
                padding: "0 5px",
                boxSizing: "border-box",
                lineHeight: "unset"
            }
        },
    }

    return (
        <Header>
            {
                !!props.dragHandleRef && (
                    <GrabHandleContainer ref={props.dragHandleRef} disabled={!props.dragHandleRef}>
                        <Icon iconName="CalculatorEqualTo" />
                    </GrabHandleContainer>
                )
            }
            <LabelContainer>
                {props.label}
            </LabelContainer>
            <IconButtonsContainer>
                {
                    !!props.onExamplesClick &&
                    <TooltipHost content='View examples'>
                        <DgIconButton
                            iconName={dgIconNames.DgExampleChunks}
                            height={13}
                            width={18}
                            color={themeBlue}
                            onClick={props.onExamplesClick}
                            label='' />
                    </TooltipHost>

                }
                {
                    <CommandBar
                        items={props.commands ?? []}
                        overflowItems={props.overflowCommands}
                        styles={commandBarStyles}
                        overflowButtonProps={{
                            styles: overflowButtonStyles,
                            menuIconProps: {
                                iconName: 'MoreVertical'
                            },
                        }} />
                }
            </IconButtonsContainer>
        </Header>
    );
}

const overflowButtonStyles = {
    root: {
        minWidth: 'unset',
        padding: '5px',
        ".ms-Icon": {
            fontWeight: '800',
        }
    },
    menuIcon: {
        margin: 0,
        fontSize: '16px'
    },
}
